import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import { ApiError, PaginatedApiResult, Pbx } from '@mangovoice/types'

export const useGetPbxList = ({
  onQuerySuccess,
  options = {},
  apiParams = {},
}: {
  onQuerySuccess?: (data: PaginatedApiResult<Pbx>) => void
  options?: Omit<UseQueryOptions<PaginatedApiResult<Pbx>, ApiError>, 'queryKey'>
  apiParams?: { limit?: number; search?: string }
}) => {
  const api = useMangoApiClient()
  return useQuery<PaginatedApiResult<Pbx>, ApiError>({
    queryKey: ['userPbxList', { apiParams }],
    queryFn: async () => {
      const { ok, data } = await api.pbxes.getUserPbxList({
        limit: apiParams.limit ?? 100,
        ...apiParams,
      })

      if (!ok) {
        return Promise.reject(data)
      }
      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    retry: options.retry ?? false,
  })
}

const DEFAULT_PBX_PAGE_SIZE = 100

export const useGetPbxListInfinite = ({
  search,
  enabled = true,
  pageSize = DEFAULT_PBX_PAGE_SIZE,
}: {
  search?: string
  enabled?: boolean
  pageSize?: number
}) => {
  const api = useMangoApiClient()
  return useInfiniteQuery<PaginatedApiResult<Pbx>, ApiError>({
    queryKey: ['userPbxListInfinite', { search, pageSize }],
    queryFn: async ({ pageParam = 0 }) => {
      const { ok, data } = await api.pbxes.getUserPbxList({
        limit: pageSize,
        offset: pageParam as number,
        ...(search ? { search } : {}),
      })
      if (!ok) return Promise.reject(data)
      return data!
    },
    getNextPageParam: (lastPage) => {
      const parts = getPaginatedResultParts(lastPage)
      return parts?.hasNextPage ? parts.nextPageOffset : undefined
    },
    initialPageParam: 0,
    enabled,
    retry: false,
  })
}

interface UseGetPbxDetailParams {
  pbxId: string
  options?: Omit<UseQueryOptions<Pbx, ApiError>, 'queryKey'>
}
export const useGetPbx = ({ pbxId, options = {} }: UseGetPbxDetailParams) => {
  const api = useMangoApiClient()
  return useQuery<Pbx, ApiError>({
    queryKey: ['pbx', pbxId],
    queryFn: async () => {
      const { ok, data } = await api.pbxes.getPbx(pbxId)
      if (!ok) {
        return Promise.reject(data)
      }
      return data!
    },
    ...options,
    retry: options.retry ?? false,
  })
}
